import type { ReactNode } from 'react';

/**
 * `.stat-row` from the design — 4-up grid of stats, full-bleed band with
 * hairline-strong top + bottom rules. Each `<Stat>` is a flex column with
 * smallcaps label, serif 42px value, and a meta line.
 *
 * Per "design not content": if a stat would render empty/null because the
 * data isn't there, render the value as "—" rather than 0. The design's
 * stats are decisional surfaces; "0" implies certainty (we have zero PCOs)
 * vs "—" implies "not yet known."
 */
export function StatRow({
  children,
  cols,
}: {
  children: ReactNode;
  /** Override the default 4-up grid. 2 or 3 columns when fewer stats are
   *  meaningful for the surface (e.g. Budget + Spent only on PCO Detail);
   *  5 for the PCO Detail money band, which wraps 3+2 below 1500px rather
   *  than squeezing five dollar figures onto a laptop. */
  cols?: 2 | 3 | 4 | 5;
}) {
  const cls = cols && cols !== 4 ? `stat-row cols-${cols}` : 'stat-row';
  return <div className={cls}>{children}</div>;
}

export interface StatProps {
  label: string;
  /** The big serif number. Pass a string so the caller can format ("$48,210", "312.5"). */
  value: ReactNode;
  /** Optional small unit suffix (e.g. " hrs"), rendered in sans + ink-3. */
  unit?: ReactNode;
  /** Optional sub-line below the value. Pass mono pieces inside as needed. */
  meta?: ReactNode;
}

export function Stat({ label, value, unit, meta }: StatProps) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">
        {value}
        {unit && <span className="unit">{unit}</span>}
      </div>
      {meta && <div className="stat-meta">{meta}</div>}
    </div>
  );
}
